/**
 * Natural-language command bar — `POST /api/v1/ai/command`.
 *
 * The endpoint always returns 200 with a structured payload. Failure
 * states (permission denial, unknown intent, executor crash) are
 * encoded in `type` so the SPA can render them inline rather than
 * showing a generic error toast.
 *
 * Two response shapes coexist:
 *   - Legacy: ``type / text / data / visualization / route /
 *     suggestions / requires_confirmation``. The classifier-then-
 *     dispatch service still produces this for follow-up exchanges
 *     it owns.
 *   - V2 (new autonomous engine): adds ``sections[]`` — a typed
 *     union of bento cards (stats, bar/pie/line charts, tables,
 *     text). The SPA's ``<ReportRenderer>`` walks the array and
 *     renders each section. ``actions[]`` is a row of buttons
 *     ("Download PDF", "Schedule weekly") shown below the report.
 *
 * Code that pre-dates V2 keeps working — ``sections`` defaults to
 * ``[]`` on legacy responses.
 */
import client, { ensureCsrfToken, getAccessToken } from "./client";

export type CommandType =
  | "query"
  | "action"
  | "report"
  | "navigate"
  | "agent"
  | "denied"
  | "error"
  | "unknown";

// ── Section shapes ──────────────────────────────────────────────

export interface StatsSection {
  type: "stats";
  title?: string;
  stats: { label: string; value: string | number | null }[];
}

export interface ChartSection {
  type: "bar" | "pie" | "line";
  title?: string;
  data: { label: string; value: number }[];
}

export interface TableSection {
  type: "table";
  title?: string;
  columns: string[];
  rows: string[][];
}

export interface TextSection {
  type: "text";
  title?: string;
  body: string;
}

/** Editable onboarding confirmation card. Rendered by ReportRenderer
 *  as a form (email / manager / start-date editable); Confirm POSTs
 *  the edited fields to /onboarding/execute (a single atomic server
 *  operation — NOT another command round trip). */
export interface OnboardEmployee {
  first_name: string;
  last_name: string;
  title: string;
  department: string | null;
  department_id: string | null;
  /** Intentionally blank — the operator types it. */
  email: string;
  /** Suggested address (org domain pattern) shown as a greyed
   *  placeholder hint, not pre-filled. */
  email_placeholder?: string;
  manager: string | null;
  manager_id: string | null;
  start_date: string; // ISO yyyy-mm-dd
  employment_type: string;
}

export interface OnboardManagerCandidate {
  id: string;
  name: string;
  title: string;
}

export interface OnboardConfirmationSection {
  type: "onboard_confirmation";
  title?: string;
  employee: OnboardEmployee;
  editable_fields: string[];
  manager_candidates: OnboardManagerCandidate[];
  steps_preview: string[];
}

export type ReportSection =
  | StatsSection
  | ChartSection
  | TableSection
  | TextSection
  | OnboardConfirmationSection;

/** A numbered source the agent referenced in the response. The
 *  ``summary`` and ``text`` section bodies contain ``[N]`` tokens
 *  that the renderer turns into clickable footnotes pointing at
 *  the matching ``ref``. ``url`` may be empty when no deep link
 *  exists (the footnote then renders as plain text). */
/** Inline base64 image generated by Claude's code-execution tool
 *  (typically matplotlib). The SPA renders via
 *  ``<img src="data:{media_type};base64,{data}">``. */
export interface GeneratedImage {
  media_type: string;
  data: string;
}

/** Downloadable file produced by an Anthropic document skill
 *  (xlsx / pptx / pdf / docx). The base64 payload is materialised
 *  to a blob URL in the SPA at render time so we never need to
 *  store the file server-side. */
export interface GeneratedFile {
  file_id: string;
  name: string;
  media_type: string;
  size: number;
  data_base64: string;
}

export interface Citation {
  ref: number;
  label: string;
  url: string;
  source_type:
    | "document"
    | "announcement"
    | "job_posting"
    | "template"
    | "employee"
    | "report"
    | "policy"
    | "leave"
    | "invoice"
    | "complaint"
    | "external"
    | "other";
}

export interface CommandResponse {
  type: CommandType;
  text: string;
  /** Free-shape payload — varies by intent (legacy executors). */
  data?: unknown;
  /** Chart spec for legacy reports; V2 uses ``sections`` instead. */
  visualization?: { chart: string; series: { label: string; value: number }[] } | null;
  /** When non-null, the SPA should `navigate(route)`. */
  route: string | null;
  /** True when the SPA must show a confirm modal before re-POSTing
   *  with `confirmed: true`. */
  requires_confirmation: boolean;
  /** 2-3 follow-up prompts shown as clickable chips. */
  suggestions: string[];
  intent?: string | null;
  conversation_id: string | null;
  /** V2 engine: structured report sections rendered by
   *  ``<ReportRenderer>``. Always at least ``[]`` on V2; absent
   *  or empty on legacy responses. */
  sections?: ReportSection[];
  /** V2 engine: action button labels shown below the report
   *  ("Download PDF", "Email to team", "Schedule weekly"). The
   *  SPA wires the click handlers locally. */
  actions?: string[];
  /** V2 engine: numbered sources referenced in the response. The
   *  agent inlines ``[N]`` tokens in ``text`` and section bodies;
   *  the renderer turns them into clickable footnotes. */
  citations?: Citation[];
  /** Phase 8 (Claude Skills + Code Execution): base64-encoded
   *  images Claude generated via matplotlib/code execution.
   *  Rendered inline as ``<img src="data:...">``. */
  images?: GeneratedImage[];
  /** Phase 8: downloadable files produced by the document skills
   *  (xlsx / pptx / pdf / docx). Each carries an inline base64
   *  payload; the SPA materialises a blob URL for download. */
  files?: GeneratedFile[];
  /** V2 engine: optional debug payload (tools called + timings).
   *  Hidden by default; surfaced behind a dev toggle. */
  debug?: {
    tools_called: number;
    loops: number;
    log: { name: string; params: unknown; elapsed_ms: number; ok: boolean }[];
  } | null;
  /** ── AI credit accounting ──
   *  Set on every successful command response. ``credits_used`` is
   *  what THIS turn cost; ``credits_remaining`` is the new total
   *  (monthly remaining + purchased balance) after the deduction.
   *  Both null on internal orgs that skipped the credit system and
   *  on legacy responses from before credits shipped.
   */
  credits_used?: number | null;
  credits_remaining?: number | null;
  /** Set only on credit-denied responses (``type === "denied"``
   *  with this set means "you don't have enough credits"). Drives
   *  the inline "Buy more credits" CTA the SPA renders. */
  credits_needed?: number | null;
  credits_available?: number | null;
  show_upgrade?: boolean;
  /**
   * Reframe — the command bar is a shortcut, the feature is
   * always reachable through the UI. ``redirect_path`` is the
   * breadcrumb to the screen that does the same job (e.g.
   * "People → Add Employee"); ``action_verb`` is the verb phrase
   * for templated copy ("onboard employees"). Both populated on
   * credit-denied responses AND on the high-cost confirmation
   * pause, so the SPA can render the reassurance line + the
   * "Go to {path}" button.
   */
  redirect_path?: string | null;
  action_verb?: string | null;
  /**
   * Tier-3 commands (15+ credits) pause for explicit confirmation
   * before the engine spends credits. When this is True the
   * response carries ``credits_needed`` + ``redirect_path`` and
   * the SPA shows the three-button card: Confirm (re-issues
   * with ``confirmed: true``) / Go to UI / Cancel.
   */
  requires_credit_confirmation?: boolean;
}

export interface CommandRequest {
  command: string;
  conversation_id?: string | null;
  confirmed?: boolean;
}

export async function runCommand(
  payload: CommandRequest,
): Promise<CommandResponse> {
  const res = await client.post<CommandResponse>("/ai/command", payload);
  return res.data;
}

// ── Streaming variant ─────────────────────────────────────────────

/** Progress events emitted by ``POST /ai/command/stream``. The
 *  ``final`` event carries the same payload as the non-streaming
 *  endpoint; the others are progress markers we surface in the UI
 *  so the user sees the engine working through tool calls. */
export type CommandStreamEvent =
  | { kind: "planning"; message: string }
  | { kind: "tool_start"; tool: string; params: unknown }
  | { kind: "tool_done"; tool: string; elapsed_ms: number; ok: boolean }
  // ``synth`` carries incremental generated text during a drafting
  // command (PIP, policy, email, etc.). ``text_delta`` is one chunk of
  // the document as Sonnet writes it; concatenating every synth event's
  // ``text_delta`` in order reproduces the full draft, so the UI can
  // render it building in real time instead of waiting for ``final``.
  | { kind: "synth"; message: string; text_delta?: string }
  // Heartbeat — fires within the first second AND every ~10s
  // thereafter (server-side, route-level) so Render's reverse proxy
  // + Cloudflare don't drop the idle SSE connection ("stays
  // thinking and never finishes" bug). The SPA can either ignore
  // these or surface ``elapsed_s`` / ``message`` as a progress
  // indicator (e.g. "Still working… (23s)").
  | {
      kind: "heartbeat";
      message: string;
      status?: "thinking";
      /** Seconds since the SSE stream opened. Useful for the
       *  command bar's "Processing… 23s" copy. */
      elapsed_s?: number;
    }
  | { kind: "final"; payload: CommandResponse }
  | {
      kind: "error";
      message: string;
      /** Optional: exception class name from the backend. */
      error_kind?: string;
      /** Optional: trimmed Python traceback (last ~2000 chars).
       *  Logged to the console in `streamCommand` for debugging. */
      traceback?: string;
    };

/** Fire a streaming command. ``onEvent`` is called once per progress
 *  event; the promise resolves with the final ``CommandResponse``.
 *
 *  Uses ``fetch`` + ``ReadableStream`` so we can POST a body with
 *  the command text — EventSource only supports GET. The server
 *  emits NDJSON (newline-delimited JSON), one event per line.
 *
 *  ``signal`` is forwarded to ``fetch`` so the caller can abort the
 *  stream mid-execution (UI "Stop" button). When aborted, the
 *  returned promise rejects with an ``AbortError``; the caller is
 *  expected to swallow that and treat the turn as cancelled. */
export async function streamCommand(
  payload: CommandRequest,
  onEvent: (ev: CommandStreamEvent) => void,
  signal?: AbortSignal,
): Promise<CommandResponse> {
  // Pull the auth + base URL from the same client our axios uses
  // so cookies / CSRF / withCredentials all line up.
  const baseURL = client.defaults.baseURL ?? "";
  const url = `${baseURL.replace(/\/$/, "")}/ai/command/stream`;

  // Build headers manually — raw fetch bypasses the axios request
  // interceptor. We need to mirror its CSRF behavior here. The
  // ``access_token`` cookie is attached automatically by the
  // browser thanks to ``credentials: "include"`` below — we do NOT
  // read a bearer token from JS anymore (post-Phase-9 the access
  // token lives in an httpOnly cookie and is not accessible to JS).
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/x-ndjson",
  };
  // Bearer fallback. When the SPA has an in-memory access token
  // (login response carried it because the httpOnly cookie got
  // dropped by iOS Safari ITP / Brave / private-mode), attach it
  // as ``Authorization: Bearer``. The CSRF middleware skips its
  // check when a Bearer header is present (Bearer tokens aren't
  // CSRF-attackable), so this is the cleanest auth path for
  // cookie-eating browsers — no separate CSRF round-trip needed.
  const bearer = getAccessToken();
  if (bearer) {
    headers["Authorization"] = `Bearer ${bearer}`;
  } else {
    // Cookie path. Cross-origin-aware CSRF token fetch —
    // ``ensureCsrfToken`` (async) hits ``/auth/csrf`` on first
    // call and caches the result. The middleware requires the
    // double-submit header on cookie-authed POSTs.
    const csrf = await ensureCsrfToken();
    if (csrf) {
      headers["X-CSRF-Token"] = csrf;
    }
  }

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok) {
    // Surface auth failures with a more useful message — "stream
    // returned 401" is meaningless to a user. Most-likely cause is
    // an expired access token; the SPA's axios refresh flow doesn't
    // run on raw fetch so the user needs to reload to get a fresh
    // token.
    if (res.status === 401) {
      throw new Error(
        "Stream auth rejected (401). Your session may have " +
          "expired — try reloading the page.",
      );
    }
    if (res.status === 403) {
      throw new Error(
        "Stream forbidden (403). You may be missing permissions for " +
          "this action.",
      );
    }
    throw new Error(`stream returned ${res.status}`);
  }
  if (!res.body) {
    throw new Error("stream body missing");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  let final: CommandResponse | null = null;
  // Capture any error event so we can throw with the real message
  // instead of the meaningless "stream ended without a final event".
  // The backend emits ``{kind: "error", message, error_kind?}`` when
  // ``stream_command`` raises — we want the user to see that, not
  // a generic timeout-shaped error.
  let errorMessage: string | null = null;

  const handleEvent = (ev: CommandStreamEvent) => {
    onEvent(ev);
    if (ev.kind === "final") {
      final = ev.payload;
    } else if (ev.kind === "error") {
      errorMessage = ev.message || "Unknown stream error";
      // Log the full traceback to devtools when the backend
      // includes one — pinpoints the exact line that crashed.
      if (ev.traceback) {
        console.group(`[streamCommand] ${ev.error_kind || "Error"}`);
        console.error(ev.message);
        console.error(ev.traceback);
        console.groupEnd();
      }
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // NDJSON: split on newline; the last partial line stays in buf
    // until the next chunk completes it.
    let idx = buf.indexOf("\n");
    while (idx !== -1) {
      const line = buf.slice(0, idx).trim();
      buf = buf.slice(idx + 1);
      if (line) {
        try {
          const ev = JSON.parse(line) as CommandStreamEvent;
          handleEvent(ev);
        } catch {
          // Tolerate a stray malformed line — the next event will
          // restore order.
        }
      }
      idx = buf.indexOf("\n");
    }
  }
  // Flush any trailing partial line.
  if (buf.trim()) {
    try {
      const ev = JSON.parse(buf.trim()) as CommandStreamEvent;
      handleEvent(ev);
    } catch {
      /* ignore */
    }
  }
  // Error event takes precedence over a missing final — the actual
  // failure reason is more useful than "stream ended without final".
  if (errorMessage) {
    throw new Error(errorMessage);
  }
  if (!final) {
    throw new Error(
      "Stream ended without a final event. The backend likely " +
        "crashed before yielding a result. Check server logs.",
    );
  }
  return final;
}

// ── Schedule a recurring run ─────────────────────────────────────

export interface ScheduledCommandPayload {
  command: string;
  cadence: "daily" | "weekly" | "monthly" | "once";
  label?: string;
  delivery?: "email" | "in_app";
}

export interface ScheduledCommand {
  id: string;
  label: string;
  command_text: string;
  cadence: string;
  delivery: string;
  next_run_at: string | null;
  last_run_at: string | null;
  last_status: string | null;
  is_active: boolean;
}

export async function scheduleCommand(
  payload: ScheduledCommandPayload,
): Promise<{ id: string; label: string; cadence: string; next_run_at: string }> {
  const res = await client.post("/ai/command/schedule", payload);
  return res.data;
}

export async function listScheduledCommands(): Promise<ScheduledCommand[]> {
  const res = await client.get<ScheduledCommand[]>("/ai/command/schedule");
  return res.data;
}

export async function deleteScheduledCommand(id: string): Promise<void> {
  await client.delete(`/ai/command/schedule/${id}`);
}
